// AGC: SorteioCompletionModal - shown when user completes all 11 quest items
// AGC: Two-step flow: (1) enter instagram + confirm follow, (2) lucky number reveal

import React, { useState } from 'react'
import { X, PartyPopper, ExternalLink } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { api, ApiError } from '../../services/api'
import LuckyNumberReveal from './LuckyNumberReveal'

interface SorteioCompletionModalProps {
  sorteioId: string
  onComplete: (luckyNumber: string) => void
  onClose: () => void
}

// AGC: Step 1 response from backend
interface CompleteQuestResponse {
  luckyNumber: string
}

// AGC: Two modal steps: instagram input → number reveal
type Step = 'instagram' | 'reveal'

export const SorteioCompletionModal: React.FC<SorteioCompletionModalProps> = ({
  sorteioId,
  onComplete,
  onClose,
}) => {
  const { t } = useTranslation()

  const [step, setStep] = useState<Step>('instagram')
  const [instagramHandle, setInstagramHandle] = useState('')
  const [followConfirmed, setFollowConfirmed] = useState(false)
  const [luckyNumber, setLuckyNumber] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [revealDone, setRevealDone] = useState(false)

  // AGC: Form is valid only when instagram filled and follow checkbox ticked
  const canSubmit = instagramHandle.trim().length > 0 && followConfirmed && !isSubmitting

  // AGC: Submit instagram handle to backend and receive lucky number
  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const result = await api.post<CompleteQuestResponse>(
        `/api/v1/sorteios/${sorteioId}/complete-quest`,
        { instagramHandle: instagramHandle.trim() },
      )
      setLuckyNumber(result.luckyNumber)
      setStep('reveal')
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMessage(err.message)
      } else {
        setErrorMessage(t('common.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Called by LuckyNumberReveal after all digits stop
  const handleRevealComplete = () => {
    setRevealDone(true)
  }

  return (
    // AGC: Dark overlay
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-gray-900 text-white shadow-2xl">

        {/* AGC: Close button — always visible */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          {/* AGC: PartyPopper icon */}
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/20">
              <PartyPopper className="h-8 w-8 text-yellow-400" />
            </div>
          </div>

          {/* AGC: ─────────── STEP 1: Instagram input ─────────── */}
          {step === 'instagram' && (
            <>
              <h2 className="mb-6 text-center text-xl font-bold text-yellow-400">
                {t('sorteio.modal.completionTitle')}
              </h2>

              {/* AGC: Instagram handle input */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-300">
                  {t('sorteio.modal.completionInstagramLabel')}
                </label>
                <input
                  type="text"
                  value={instagramHandle}
                  onChange={e => setInstagramHandle(e.target.value)}
                  placeholder={t('sorteio.modal.completionInstagramPlaceholder')}
                  className="w-full rounded-xl bg-gray-800 px-4 py-3 text-white placeholder-gray-500 outline-none ring-1 ring-gray-700 focus:ring-yellow-500 transition-all"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              {/* AGC: Follow @droidfarm button — gradient purple→pink */}
              <a
                href="https://www.instagram.com/DroidFarm"
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90"
              >
                {t('sorteio.modal.completionFollowButton')}
                <ExternalLink className="h-4 w-4" />
              </a>

              {/* AGC: Confirm follow checkbox */}
              <label className="mb-5 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={followConfirmed}
                  onChange={e => setFollowConfirmed(e.target.checked)}
                  className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer accent-yellow-500"
                />
                <span className="text-sm text-gray-300">
                  {t('sorteio.modal.completionConfirmFollow')}
                </span>
              </label>

              {/* AGC: Error message */}
              {errorMessage && (
                <p className="mb-3 rounded-lg bg-red-900/40 px-4 py-2 text-sm text-red-400">
                  {errorMessage}
                </p>
              )}

              {/* AGC: Submit button — disabled until form valid */}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={[
                  'flex w-full items-center justify-center rounded-xl px-4 py-3 font-semibold transition-all',
                  canSubmit
                    ? 'bg-yellow-500 text-black hover:bg-yellow-400 active:bg-yellow-600'
                    : 'cursor-not-allowed bg-gray-700 text-gray-500',
                ].join(' ')}
              >
                {isSubmitting ? t('common.loading') : t('sorteio.modal.completionSubmit')}
              </button>
            </>
          )}

          {/* AGC: ─────────── STEP 2: Lucky number reveal ─────────── */}
          {step === 'reveal' && luckyNumber !== null && (
            <>
              <h2 className="mb-6 text-center text-xl font-bold text-yellow-400">
                {t('sorteio.modal.revealTitle')}
              </h2>

              <div className="mb-6">
                <LuckyNumberReveal number={luckyNumber} onComplete={handleRevealComplete} />
              </div>

              {/* AGC: Success message with confetti and good luck wish after reveal */}
              {revealDone && (
                <div className="animate-fadeIn">
                  {/* AGC: Celebration emojis */}
                  <div className="mb-3 flex justify-center gap-2 text-3xl animate-bounce">
                    <span>🎉</span><span>🍀</span><span>🎊</span>
                  </div>
                  <p className="mb-2 text-center text-lg font-bold text-green-400">
                    {t('sorteio.modal.revealGoodLuck')}
                  </p>
                  <p className="mb-5 text-center text-sm text-gray-400">
                    {t('sorteio.modal.revealKeepNumber')}
                  </p>
                  <button
                    onClick={() => onComplete(luckyNumber)}
                    className="flex w-full items-center justify-center rounded-xl bg-yellow-500 px-4 py-3 font-semibold text-black hover:bg-yellow-400 active:bg-yellow-600 transition-colors"
                  >
                    {t('common.ok')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SorteioCompletionModal
